/**
 * Phase 66 Plan 02 — typed auth endpoints.
 * Phase 69 Plan 06 — extended with optional `totp_code` for D-2FA-01.
 *
 * Port of `js/app.js` lines 51-80 (login) + lines 116-123 (logout).
 * Backend contract (Phase 56 + 57 + 63 issuers + Phase 69 Plan 03 totp branch):
 *   POST /auth/login   { email, password }                   → 200 { access_token, token_type }
 *                      { email, password, totp_code? }
 *                                                              401 { detail: "Invalid credentials" }
 *                                                              401 { detail: "totp_required" }
 *                                                              401 { detail: "totp_invalid" }
 *                                                              401 { detail: "account_disabled" }
 *                                                              500 { detail: "2fa_misconfigured" }
 *   POST /auth/logout                                         → 204 (best-effort; ignore errors)
 */

import { apiPost } from "./client";

export type LoginResponse = {
  access_token: string;
  token_type?: string;
  /**
   * Legacy fallback — backend currently emits role inside JWT claims, but
   * Phase 65 noted it may also surface here. AuthProvider falls back to
   * `decodeJwtPayload(access_token).role` first, then this field.
   */
  role?: string;
  /**
   * Phase 69 live-prod follow-up: backend echoes the user's current 2FA
   * state so the Settings → 2FA card renders the correct badge on first
   * mount. Optional in the type so older backends that pre-date the
   * follow-up still parse cleanly (badge defaults to "Disabilitato").
   */
  totp_enabled?: boolean;
};

export function loginApi(
  email: string,
  password: string,
  totp_code?: string,
): Promise<LoginResponse> {
  // skipAuthLogout: a 401 from /auth/login is "wrong credentials" /
  // "totp_required" / "totp_invalid" / "account_disabled" — none of those
  // mean "session expired". The generic 401 handler would dispatch
  // authBus logout and rewrite the error message; opting out keeps
  // body.detail intact so LoginPage can branch on the contract strings.
  // See `RequestOpts.skipAuthLogout` jsdoc.
  const body: { email: string; password: string; totp_code?: string } = {
    email,
    password,
  };
  if (totp_code) body.totp_code = totp_code;
  return apiPost<LoginResponse>("/auth/login", body, {
    skipAuthLogout: true,
  });
}

/**
 * Best-effort logout. Errors are intentionally swallowed — the local
 * token has already been cleared by AuthProvider before this call.
 */
export function logoutApi(): Promise<unknown> {
  return apiPost("/auth/logout").catch(() => null);
}
